import React, { useMemo } from 'react';
import {
  useReservoir,
  useLegacyAchievements,
} from '../../hooks/usePlayerState';
import './AchievementsOrb.css';

const VALID_LEGACY_ACHIEVEMENT_IDS = new Set([
  'first_dose', 'pharmacist_in_training', 'drug_expert',
  'first_blood', 'boss_slayer', 'flawless_victory',
  'phantom_hunter', 'phantom_conqueror',
  'studious', 'procrastinator',
  'quiz_master', 'daily_grind', 'osmotic_awakening',
  'study_session_5', 'drug_game_10', 'streak_master', 'card_collector_15', 'fountain_5',
  'centurion',
  'first_duel', 'arena_veteran', 'elo_climber', 'apex_predator',
  'honorable',
]);

const KEYSTONE_SLUGS = new Set(['vancomycin', 'dupilumab', 'metformin']);

function normalizeSlug(name) {
  return String(name || '').toLowerCase().replace(/[^a-z0-9_]+/g, '-').replace(/^-+|-+$/g, '');
}

function classifyGate(entry, legacyAchievements) {
  const slug = normalizeSlug(entry.generic_name);
  const acquisition = entry.acquisition || {};
  const reqAch = acquisition.required_achievement;

  if (KEYSTONE_SLUGS.has(slug)) return { kind: 'genesis' };
  if (entry.is_starter) return { kind: 'starter' };
  if (entry.achievement_json_present) return { kind: 'criterion' };
  if (reqAch) {
    if (!VALID_LEGACY_ACHIEVEMENT_IDS.has(reqAch)) return { kind: 'broken' };
    const earned = legacyAchievements.find((achievement) => achievement.id === reqAch)?.earned;
    return { kind: earned ? 'legacy-earned' : 'legacy-locked' };
  }
  return { kind: 'open' };
}

function ownedStatus(entry) {
  if (entry.user_status === 'golden') return 'owned';
  if (entry.is_chosen_starter) return 'owned';
  return 'unowned';
}

export default function AchievementsOrb({ hidden = false, active = false, onActivate, hint = false }) {
  const { data: reservoir = [] } = useReservoir();
  const { data: legacy = [] } = useLegacyAchievements();

  const totals = useMemo(() => {
    const enriched = reservoir.map((entry) => ({
      ...entry,
      slug: normalizeSlug(entry.generic_name),
      achievement_json_present: !!entry.achievement && Object.keys(entry.achievement).length > 0,
    }));

    return {
      cards: enriched.length,
      owned: enriched.filter((entry) => ownedStatus(entry) === 'owned').length,
      broken: enriched.filter((entry) => classifyGate(entry, legacy).kind === 'broken').length,
    };
  }, [reservoir, legacy]);

  if (hidden) return null;

  const badgeCount = totals.broken;
  // Attention cue for first-time (anon landing) visitors: a pulsing violet halo
  // + a small "Skill Tree" label so they know to click it. Suppressed once the
  // tree is open (`active`) and dismissed by the parent on first click (`hint`).
  const showHint = hint && !active;

  return (
    <button
      className={`ach-orb ${active ? 'ach-orb--active' : ''} ${showHint ? 'ach-orb--hint' : ''}`}
      onClick={onActivate}
      aria-label={`Open skill tree, ${totals.owned}/${totals.cards} cards owned`}
      type="button"
    >
      {showHint && <span className="ach-orb-hint-pulse" aria-hidden="true" />}
      <span className="ach-orb-ring" aria-hidden="true" />
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22V12" />
        <path d="M12 12L7 7" />
        <path d="M12 12L17 7" />
        <path d="M7 7V3" />
        <path d="M17 7V3" />
        <path d="M12 12L12 5" />
      </svg>
      {badgeCount > 0 && (
        <span className="ach-orb-badge" aria-hidden="true">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </button>
  );
}
